import React from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import { Newspaper, Person } from "@mui/icons-material";

/**
 * Compact navigation card for the patient dashboard.
 *
 * Always visible — gives first-time and returning patients a clear
 * entry point regardless of appointment state.
 *
 * Align the `to` paths with the real router entries before shipping.
 */
const LINKS = [
  {
    key: "find",
    label: "Find a doctor",
    description: "Browse specialists",
    icon: <SearchOutlinedIcon fontSize="small" />,
    to: "/doctors",
  },
  {
    key: "appointments",
    label: "My appointments",
    description: "Upcoming & past",
    icon: <EventNoteOutlinedIcon fontSize="small" />,
    to: "/appointments",
  },
  {
    key: "profile",
    label: "My Profile",
    description: "Details and Profile Info",
    icon: <Person fontSize="small" />,
    to: "/profile",
  },
  {
    key: "news",
    label: "News",
    description: "Blog articles & news",
    icon: <Newspaper fontSize="small" />,
    to: "/news",
  },
];

export default function PatientQuickActions() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: "var(--dm-radius)",
        backgroundColor: "var(--dm-white)",
        border: "1px solid var(--dm-mist)",
        boxShadow: "var(--dm-shadow)",
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{ color: "var(--dm-ink)" }}
        >
          Quick actions
        </Typography>
        <Typography variant="caption" sx={{ color: "var(--dm-muted)" }}>
          All about health
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.25,
        }}
      >
        {LINKS.map((link) => (
          <ButtonBase
            key={link.key}
            onClick={() => navigate(link.to)}
            aria-label={`Open ${link.label}`}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: "1px solid var(--dm-mist)",
              textAlign: "left",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              display: "flex",
              gap: 1.25,
              transition:
                "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
              "&:hover": {
                borderColor: "var(--dm-blue)",
                backgroundColor: "rgba(0, 64, 179, 0.03)",
              },
              "&:active": { transform: "translateY(1px)" },
              "&:focus-visible": {
                outline: "3px solid var(--dm-sky)",
                outlineOffset: 2,
              },
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 64, 179, 0.08)",
                color: "var(--dm-blue)",
                flexShrink: 0,
              }}
            >
              {link.icon}
            </Box>

            <Box minWidth={0}>
              <Typography
                fontWeight={600}
                sx={{ color: "var(--dm-ink)", fontSize: "0.9rem" }}
              >
                {link.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "var(--dm-muted)",
                  display: "block",
                  lineHeight: 1.35,
                }}
              >
                {link.description}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}